import { useEffect, useState } from 'react';
import { useApi } from '@/lib/api';
import { useMe } from '@/lib/useMe';
import { MeGate } from '@/components/MeGate';
import { CoreIcon } from '@/components/CoreIcon';
import { cn } from '@/lib/utils';

interface Member {
  id: string;
  email: string;
  name: string | null;
  role: 'admin' | 'agent' | 'viewer';
  joined_at: number;
}

interface AuditEntry {
  id: string;
  actor: string;
  action: string;
  ticket_id: string | null;
  meta: string | null;
  created_at: number;
}

export function Team() {
  const meStatus = useMe();
  return <MeGate status={meStatus}>{(me) => <TeamBody orgId={me.org!.id} />}</MeGate>;
}

function TeamBody({ orgId }: { orgId: string }) {
  const api = useApi();
  const [members, setMembers] = useState<Member[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [loadingAudit, setLoadingAudit] = useState(true);
  const [tab, setTab] = useState<'members' | 'audit'>('members');

  useEffect(() => {
    setLoadingMembers(true);
    api.get<{ members: Member[] }>(`/api/orgs/${orgId}/team/members`)
      .then((r) => setMembers(r.members))
      .catch(console.error)
      .finally(() => setLoadingMembers(false));

    setLoadingAudit(true);
    api.get<{ entries: AuditEntry[] }>(`/api/orgs/${orgId}/team/audit`)
      .then((r) => setAudit(r.entries))
      .catch(console.error)
      .finally(() => setLoadingAudit(false));
  }, [api, orgId]);

  return (
    <div className="h-full overflow-y-auto">
      <header className="p-6 border-b bg-background">
        <h1 className="text-xl font-semibold">Team</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage members and review activity across your workspace.
        </p>
      </header>

      <div className="p-6 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-[380px_minmax(0,1fr)] gap-6">
          {/* ── Members panel ── */}
          <div className="card flex flex-col">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="mono-label flex items-center gap-1.5">
                <CoreIcon name="people" size={12} />
                Members
              </div>
              <span className="text-[11px] text-muted-foreground tabular-nums">
                {members.length}
              </span>
            </div>

            <div className="flex-1 overflow-y-auto max-h-[500px]">
              {loadingMembers ? (
                <SkeletonMembers />
              ) : members.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  No team members found. Members are synced from Clerk when they sign in.
                </div>
              ) : (
                members.map((m) => (
                  <div key={m.id} className="px-4 py-3 border-b border-border last:border-0 flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-foreground text-surface grid place-items-center text-[12px] font-semibold uppercase shrink-0">
                      {(m.name ?? m.email).charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium truncate">
                        {m.name || m.email}
                      </div>
                      {m.name && (
                        <div className="text-[11px] text-muted-foreground truncate">{m.email}</div>
                      )}
                    </div>
                    <RoleBadge role={m.role} />
                  </div>
                ))
              )}
            </div>

            <div className="p-4 border-t border-border bg-muted/30">
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Members and roles are managed via Clerk Organizations. Use the org switcher in the sidebar to invite teammates.
              </p>
            </div>
          </div>

          {/* ── Audit log panel ── */}
          <div className="card flex flex-col">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="mono-label flex items-center gap-1.5">
                <CoreIcon name="history" size={12} />
                Audit log
              </div>
              <span className="text-[11px] text-muted-foreground tabular-nums">
                {audit.length} entries
              </span>
            </div>

            <div className="flex-1 overflow-y-auto max-h-[600px]">
              {loadingAudit ? (
                <SkeletonAudit />
              ) : audit.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  No audit entries yet. Actions like draft approvals, tool toggles, and settings changes are logged here.
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {audit.map((entry) => (
                    <AuditRow key={entry.id} entry={entry} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RoleBadge({ role }: { role: string }) {
  const styles =
    role === 'admin'
      ? 'bg-accent/10 text-accent border-accent/20'
      : role === 'agent'
      ? 'bg-foreground/5 text-foreground border-border'
      : 'bg-muted text-muted-foreground border-border';
  return (
    <span className={cn('inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-mono shrink-0', styles)}>
      {role}
    </span>
  );
}

function MetaPreview({ json }: { json: string | null }) {
  if (!json) return null;
  const parsed = safeParse(json) as Record<string, unknown> | null;
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const keys = Object.keys(parsed).slice(0, 3);
  if (keys.length === 0) return null;
  return (
    <div className="text-[11px] text-muted-foreground mt-0.5">
      {keys.map((k) => (
        <span key={k} className="mr-3">{k}: {String(parsed[k])}</span>
      ))}
    </div>
  );
}

function AuditRow({ entry }: { entry: AuditEntry }) {
  const icon = actionIcon(entry.action);

  return (
    <div className="px-4 py-3 flex items-start gap-3">
      <div className="mt-0.5 h-6 w-6 rounded-sm bg-muted grid place-items-center shrink-0">
        <CoreIcon name={icon} size={12} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px]">
          <span className="font-medium">{formatActor(entry.actor)}</span>
          {' '}
          <span className="text-muted-foreground">{formatAction(entry.action)}</span>
        </div>
        {entry.ticket_id && (
          <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
            Ticket: {entry.ticket_id}
          </div>
        )}
        <MetaPreview json={entry.meta} />
      </div>
      <span className="text-[10px] text-muted-foreground shrink-0 tabular-nums">
        {relativeTime(entry.created_at)}
      </span>
    </div>
  );
}

function actionIcon(action: string): string {
  if (action.includes('approve')) return 'check-alt';
  if (action.includes('reject')) return 'x-circle';
  if (action.includes('tool')) return 'puzzle';
  if (action.includes('setting')) return 'settings';
  if (action.includes('spam')) return 'warning';
  if (action.includes('resolv')) return 'check-alt';
  if (action.includes('draft')) return 'notes';
  return 'speech';
}

function formatActor(actor: string): string {
  if (actor === 'system') return 'System';
  if (actor.startsWith('user_')) return actor.slice(0, 12) + '…';
  return actor;
}

function formatAction(action: string): string {
  return action.replace(/_/g, ' ');
}

function safeParse(json: string): unknown {
  try { return JSON.parse(json); } catch { return null; }
}

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function SkeletonMembers() {
  return (
    <div className="p-4 space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="animate-pulse flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-muted" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 bg-muted rounded w-1/2" />
            <div className="h-2.5 bg-muted rounded w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

function SkeletonAudit() {
  return (
    <div className="p-4 space-y-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="animate-pulse flex items-start gap-3">
          <div className="h-6 w-6 rounded-sm bg-muted" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 bg-muted rounded w-3/4" />
            <div className="h-2.5 bg-muted rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}
